/**
 * Who may see private characters on a profile's Characters tab.
 *
 * Kept dependency-free so the visibility contract can be tested directly
 * (scripts/test-profile-character-visibility.mts).
 */

type ProfileCharacterViewer = {
  /** Username of the signed-in viewer, if any. */
  viewerUsername: string | null | undefined
  /** Auth user id of the signed-in viewer, if any. */
  viewerId: string | null | undefined
  /** Username in the profile URL. */
  profileUsername: string
  /** Id of the profile being viewed (resolved from the username). */
  profileId: string | null | undefined
}

/**
 * Only the profile owner sees their own private characters: the viewer's
 * username must match the URL and their auth user id must match the profile id.
 * Anyone else (logged out, another account, still loading) gets the public view.
 */
export function canViewPrivateCharacters({
  viewerUsername,
  viewerId,
  profileUsername,
  profileId,
}: ProfileCharacterViewer): boolean {
  if (!viewerUsername || !viewerId || !profileId) return false
  return (
    viewerUsername.toLowerCase() === profileUsername.toLowerCase() &&
    viewerId === profileId
  )
}

/** Separate cache entries for the owner view and the public view. */
export function profileCharactersQueryKey<K extends readonly string[]>(
  baseKey: K,
  includePrivate: boolean
) {
  return [...baseKey, includePrivate ? "owner" : "public"] as const
}

type FilterableQuery<Q> = { eq: (column: string, value: boolean) => Q }

/** Public view keeps the is_public filter; the owner view drops it. */
export function applyCharacterVisibility<Q extends FilterableQuery<Q>>(
  query: Q,
  includePrivate: boolean
): Q {
  return includePrivate ? query : query.eq("is_public", true)
}
